import {
  type ClaimInput,
  type ClusterRef,
  type ColorMode,
  type Entity,
  type EntityFacets,
  type EntityKind,
  type EntityProfile,
  type EntityStyle,
  type EntitySummary,
  type SchemaSnapshot,
  styleFor,
  typeChain,
  whyText,
} from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import type { EntityKindShape } from '../components/data/entity-mark';
import type { EntitySuggestion } from '../components/guidance/entity-autocomplete';
import type { RefSearch } from '../components/schema/field-input';
import { ApiError, api } from './api';
import { useColorMode } from './colors';

// Client side of the claims API for the directory and profile screens (task 9). Every read and write goes
// through /api (the claims service); nothing here touches data directly.

/** Directory tabs, in the order of PLAN §12.7. */
export const DIRECTORY_TABS: { kind: EntityKind; label: string; singular: string }[] = [
  { kind: 'actor', label: 'Actors', singular: 'actor' },
  { kind: 'event', label: 'Events', singular: 'event' },
  { kind: 'location', label: 'Places', singular: 'place' },
  { kind: 'asset', label: 'Assets', singular: 'asset' },
  { kind: 'document', label: 'Sources', singular: 'source' },
  { kind: 'case', label: 'Cases', singular: 'case' },
];

/** List fields shown as directory columns and facets when an entity type has them. */
export const DIRECTORY_FIELDS = ['camp', 'investigation_status'] as const;

/** The shape of an entity's mark: persons are circles, other actors squares (PLAN §12.2). */
export function shapeOf(schema: Pick<SchemaSnapshot, 'types'>, typeId: string, kind: EntityKind): EntityKindShape {
  if (kind !== 'actor') return kind;
  return typeChain(schema.types, typeId).some(t => t.key === 'person') ? 'actor_person' : 'actor_org';
}

export const typeLabelOf = (schema: Pick<SchemaSnapshot, 'types'>, typeId: string) => schema.types.find(t => t.id === typeId)?.label ?? '';

type Markable = Pick<EntitySummary, 'typeId' | 'kind' | 'reviewStatus' | 'sensitivity'> & {
  style?: EntityStyle | null;
  cluster?: ClusterRef | null;
};

/** Everything an EntityMark needs, in the active "Color by" mode (PLAN §12.2): one answer for every view. */
export function markProps(schema: Pick<SchemaSnapshot, 'types'>, e: Markable, mode: ColorMode = 'type') {
  const style = styleFor(mode, schema, e);
  return {
    kind: shapeOf(schema, e.typeId, e.kind),
    colorSlot: style.colorSlot,
    status: e.reviewStatus,
    confidential: e.sensitivity === 'confidential',
    why: whyText(style),
    fade: style.fade,
    ring: style.ring,
    ringSlot: style.ringSlot,
  };
}

export function toSuggestion(schema: Pick<SchemaSnapshot, 'types'>, e: EntitySummary, mode: ColorMode = 'type'): EntitySuggestion {
  const m = markProps(schema, e, mode);
  return {
    id: e.id,
    name: e.displayName,
    matchedName: e.matchedName,
    kind: m.kind,
    typeLabel: typeLabelOf(schema, e.typeId),
    colorSlot: m.colorSlot,
    why: m.why,
    fade: m.fade,
    status: e.reviewStatus,
    confidential: e.sensitivity === 'confidential',
    detail: e.code,
  };
}

export interface ListResult {
  entities: EntitySummary[];
  next: string | null;
}

/** Type-ahead across all names, aliases and scripts, optionally limited to some kinds. */
export function useEntitySearch(schema: SchemaSnapshot | undefined, exclude?: string): RefSearch {
  const { mode } = useColorMode();
  return useCallback(
    async (kinds, q) => {
      if (!schema) return [];
      const params = new URLSearchParams({ q, limit: '8' });
      if (kinds?.length) params.set('kinds', kinds.join(','));
      const res = await api<ListResult>(`/entities?${params}`).catch(() => ({ entities: [], next: null }));
      return res.entities.filter(e => e.id !== exclude).map(e => toSuggestion(schema, e, mode));
    },
    [schema, exclude, mode],
  );
}

export const profileKey = (id: string) => ['entity', id, 'profile'] as const;

export const useProfile = (id: string) =>
  useQuery({ queryKey: profileKey(id), queryFn: () => api<EntityProfile & { cached: boolean }>(`/entities/${id}/profile`) });

export const useDirectory = (params: URLSearchParams) =>
  useQuery({
    queryKey: ['entities', 'list', params.toString()],
    queryFn: () => api<ListResult>(`/entities?${params}`),
    placeholderData: prev => prev,
  });

export const useFacets = (params: URLSearchParams) =>
  useQuery({
    queryKey: ['entities', 'facets', params.toString()],
    queryFn: () => api<EntityFacets>(`/entities/facets?${params}`),
    placeholderData: prev => prev,
  });

export type Write = { path: string; method?: string; body?: unknown };

/**
 * Writes to an entity (claims, names, tags, header): refreshes this profile, profiles it links to and the
 * directory afterwards, and keeps the server's plain-language error for display.
 */
export function useEntityWrite() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: ({ path, method = 'POST', body }: Write) => api<unknown>(path, { method, body: body ?? {} }),
    onSuccess: () => {
      setError(null);
      return qc.invalidateQueries({ queryKey: ['entity'] }).then(() => qc.invalidateQueries({ queryKey: ['entities'] }));
    },
    onError: e =>
      setError(
        e instanceof ApiError ? ((e.data.errors as string[] | undefined)?.join(' ') ?? e.message) : 'Something went wrong. Try again.',
      ),
  });
  return { ...m, error, clearError: () => setError(null) };
}

export const addClaims = (entityId: string, claims: ClaimInput[]): Write => ({ path: `/entities/${entityId}/claims`, body: { claims } });
export const createEntity = (body: Record<string, unknown>) => api<Entity>('/entities', { body });
